import React from 'react';

interface TimePickerProps {
  selectedTime: string; // Time in HH:mm format (e.g., "09:30")
  onSelectTime: (time: string) => void;
  label: string;
  minTime?: string; // Minimum time in HH:mm format
  maxTime?: string; // Maximum time in HH:mm format
}

const TimePicker: React.FC<TimePickerProps> = ({
  selectedTime,
  onSelectTime,
  label,
  minTime,
  maxTime,
}) => {
  // Parse current selected time
  const [hour, minute] = selectedTime.split(':').map(Number);

  // Check if a specific time is disabled
  const isTimeDisabled = (h: number, m: number): boolean => {
    if (!minTime && !maxTime) return false;

    const timeValue = h * 100 + m;
    const [minH, minM] = minTime ? minTime.split(':').map(Number) : [0, 0];
    const [maxH, maxM] = maxTime ? maxTime.split(':').map(Number) : [23, 59];
    const minValue = minH * 100 + minM;
    const maxValue = maxH * 100 + maxM;

    return timeValue < minValue || timeValue > maxValue;
  };

  // Handle hour change
  const handleHourChange = (newHour: number) => {
    const newTime = `${newHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    onSelectTime(newTime);
  };

  // Handle minute change
  const handleMinuteChange = (newMinute: number) => {
    const newTime = `${hour.toString().padStart(2, '0')}:${newMinute.toString().padStart(2, '0')}`;
    onSelectTime(newTime);
  };

  return (
    <div>
      <h3 className="text-base font-medium text-gray-700 mb-3">{label}</h3>

      <div className="flex gap-3">
        {/* Hour Selector */}
        <div className="w-32">
          <select
            value={hour}
            onChange={(e) => handleHourChange(Number(e.target.value))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base font-medium"
          >
            {Array.from({ length: 15 }, (_, i) => i + 8).map((h) => {
              const disabled = isTimeDisabled(h, minute);
              return (
                <option key={h} value={h} disabled={disabled}>
                  {h.toString().padStart(2, '0')}시
                </option>
              );
            })}
          </select>
        </div>

        {/* Minute Selector */}
        <div className="w-32">
          <select
            value={minute}
            onChange={(e) => handleMinuteChange(Number(e.target.value))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base font-medium"
          >
            {[0, 30].map((m) => {
              // 22시일 때는 30분 선택 불가
              const disabled = isTimeDisabled(hour, m) || (hour === 22 && m === 30);
              return (
                <option key={m} value={m} disabled={disabled}>
                  {m.toString().padStart(2, '0')}분
                </option>
              );
            })}
          </select>
        </div>
      </div>
    </div>
  );
};

export default TimePicker;
